import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { ClaimForm, normalizeLineOfBusiness } from './CRM';
import './Dashboard.css';
import './CRM.css';
import './FnolReviewWindow.css';
import { fetchFirstFnolJson } from '../utils/fnolApi';
import { getFnolTranscript } from '../utils/fnolTranscripts';
import { fetchFnolVoiceRecordings, hasKnownVoiceRecording } from '../utils/fnolVoiceRecordings';

const firstValue = (data, keys) =>
  keys.map((key) => data?.[key]).find((value) => value !== undefined && value !== null && value !== '');

const normalizeChannel = (value = '') => String(value || '').trim().toUpperCase();
const mailClaimIds = new Set(['C-260902-002', 'C-260902-001', 'C-260901-007']);
const callClaimIds = new Set(['C-260902-003', 'C-260831-006', 'C-260902-004']);

const deriveClaimIdFromFnolId = (recordId = '') => {
  const normalized = String(recordId || '').trim().toUpperCase();
  return normalized.startsWith('F-') ? `C-${normalized.slice(2)}` : normalized;
};

const deriveFnolIdFromClaimId = (recordId = '') => {
  const normalized = String(recordId || '').trim().toUpperCase();
  return normalized.startsWith('C-') ? `F-${normalized.slice(2)}` : normalized;
};

const getLinkedRecordIds = (recordId = '', record = {}) => {
  const values = [
    recordId,
    record.fnol_id,
    record.fnolId,
    record.claim_id,
    record.claimId,
    record.claimid,
  ];
  values.forEach((value) => {
    values.push(deriveClaimIdFromFnolId(value));
    values.push(deriveFnolIdFromClaimId(value));
  });
  return Array.from(new Set(values.map((value) => String(value || '').trim().toUpperCase()).filter(Boolean)));
};

const isMailRecord = (recordId = '', record = {}) =>
  getLinkedRecordIds(recordId, record).some((value) => mailClaimIds.has(deriveClaimIdFromFnolId(value)));

const isCallRecord = (recordId = '', record = {}) =>
  getLinkedRecordIds(recordId, record).some((value) => callClaimIds.has(deriveClaimIdFromFnolId(value)));

const getRecordChannel = (recordId = '', record = {}) =>
  isCallRecord(recordId, record)
    ? 'CALL'
    : isMailRecord(recordId, record)
    ? 'EMAIL'
    : normalizeChannel(firstValue(record, ['interaction_channel', 'interactionChannel', 'Interaction_Channel']));

const setFirstPresent = (target, targetKey, values) => {
  if (target[targetKey] !== undefined && target[targetKey] !== null && target[targetKey] !== '') return;

  const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
  if (value !== undefined) target[targetKey] = value;
};

const normalizeMailRecordFields = (recordId = '', record = {}) => {
  const normalized = { ...record };

  setFirstPresent(normalized, 'claim_id', [
    normalized.claimid,
    normalized.claimId,
    deriveClaimIdFromFnolId(recordId),
  ]);
  setFirstPresent(normalized, 'fnol_id', [
    normalized.fnolId,
    deriveFnolIdFromClaimId(normalized.claim_id),
    deriveFnolIdFromClaimId(recordId),
  ]);
  setFirstPresent(normalized, 'insured_name', [
    normalized.insured,
    normalized.insured_full_name,
    normalized.customer_name,
    normalized.claimant_name,
  ]);
  setFirstPresent(normalized, 'insured', [normalized.insured_name]);
  setFirstPresent(normalized, 'address', [
    normalized.insured_address,
    normalized.claimant_address,
  ]);
  setFirstPresent(normalized, 'primary_phone', [
    normalized.insured_phone,
    normalized.phone_primary,
  ]);
  setFirstPresent(normalized, 'phone_primary', [normalized.primary_phone]);
  setFirstPresent(normalized, 'primary_email', [
    normalized.insured_email,
    normalized.email_id_primary,
    normalized.sender_email,
  ]);
  setFirstPresent(normalized, 'email_id_primary', [normalized.primary_email]);
  setFirstPresent(normalized, 'reported_by', [
    normalized.insured_name,
    normalized.sender_email,
  ]);
  setFirstPresent(normalized, 'date_of_loss', [
    normalized.date_reported,
    normalized.created_timestamp,
  ]);
  setFirstPresent(normalized, 'loss_description', [
    normalized.summary,
    normalized.raw_email_text,
  ]);
  normalized.interaction_channel = 'EMAIL';

  return normalized;
};

const getEmailSourceIds = (recordId = '', record = {}) => {
  const sourceIds = [
    recordId,
    record.fnol_id,
    record.fnolId,
    record.claim_id,
    record.claimId,
    record.claimid,
    deriveClaimIdFromFnolId(recordId),
    deriveFnolIdFromClaimId(recordId),
    record.policy_number,
    record.Policy_Number,
  ];

  return Array.from(
    new Set(sourceIds.map((value) => String(value || '').trim()).filter(Boolean))
  );
};

const loadFirstEmailSource = async (recordId = '', record = {}) => {
  let lastError = null;

  for (const sourceId of getEmailSourceIds(recordId, record)) {
    try {
      return await fetchFirstFnolJson(
        `/fnol-records/${encodeURIComponent(sourceId)}/email-source`,
        { timeoutMs: 20000 }
      );
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Unable to load email source.');
};

const buildMailRecordFromEmailSource = (recordId = '', source = {}) =>
  normalizeMailRecordFields(recordId, {
    ...source,
    insured_name: source.insured_name || source.insured || source.customer_name,
    primary_email: source.insured_email || source.primary_email || source.from,
    date_of_loss: source.date_of_loss || source.date,
    loss_description: source.loss_description || source.body,
  });

const formatTranscriptTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const formatRecordingSize = (value) => {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  return `${(size / 1024).toFixed(1)} KB`;
};

const formatRecordingName = (fileName = '') => {
  if (/^full[-_ ]?session\.wav$/i.test(fileName)) return 'Full Session';

  return String(fileName || '')
    .replace(/\.wav$/i, '')
    .replace(/-/g, ' ')
    .replace(/\b(agent|user)\b/i, (match) => match.charAt(0).toUpperCase() + match.slice(1));
};

const getDisplayVoiceRecordings = (recordings = []) => {
  const fullSessionRecordings = recordings.filter((recording) =>
    /^full[-_ ]?session\.wav$/i.test(recording?.file_name || '')
  );

  if (!fullSessionRecordings.length) return recordings;

  const sortedFullSessions = [...fullSessionRecordings].sort((a, b) => {
    const sizeDifference = Number(b?.size || 0) - Number(a?.size || 0);
    if (sizeDifference) return sizeDifference;
    return String(b?.updated || '').localeCompare(String(a?.updated || ''));
  });

  return sortedFullSessions.slice(0, 1);
};

const extractEmailBodyText = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const text = extractEmailBodyText(item);
      if (text) return text;
    }
    return '';
  }

  if (typeof value === 'object') {
    const directKeys = [
      'body',
      'email_body',
      'message_body',
      'content',
      'text',
      'message',
      'raw_text',
      'summary',
    ];
    for (const key of directKeys) {
      const text = extractEmailBodyText(value[key]);
      if (text) return text;
    }

    const nestedKeys = ['outputs', 'choices', 'messages', 'parts', 'chunks'];
    for (const key of nestedKeys) {
      const text = extractEmailBodyText(value[key]);
      if (text) return text;
    }
  }

  return '';
};

const formatEmailBody = (body = '') => {
  let text = String(body || '').trim();

  if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
    try {
      text = extractEmailBodyText(JSON.parse(text)) || text;
    } catch {
      // Fall back to the original body if the source is not valid JSON.
    }
  }

  return text
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*/g, '')
    .replace(/\n\s*-\s+/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const SourcePane = ({
  record = {},
  recordId = '',
  sourceChannel = '',
  transcript = null,
  isTranscriptLoading = false,
  transcriptError = '',
  voiceRecordings = null,
  isVoiceLoading = false,
  voiceError = '',
  emailSource = null,
  isEmailLoading = false,
  emailError = '',
}) => {
  const channel = sourceChannel || getRecordChannel(recordId, record);
  const isCall = channel === 'CALL';
  const isEmail = channel === 'EMAIL';
  const transcriptMessages = Array.isArray(transcript?.messages) ? transcript.messages : [];
  const formattedEmailBody = formatEmailBody(emailSource?.body);
  const displayVoiceRecordings = useMemo(
    () => getDisplayVoiceRecordings(voiceRecordings?.recordings || []),
    [voiceRecordings]
  );

  const renderTranscript = (loadingLabel, emptyLabel) => {
    if (isTranscriptLoading) {
      return <div className="fnol-source-empty">{loadingLabel}</div>;
    }

    if (transcript && transcriptMessages.length) {
      return (
        <div className="fnol-transcript">
          {transcript.timestamp && <div className="fnol-transcript-time">{transcript.timestamp}</div>}
          {transcriptMessages.map((message, index) => (
            <article className={`fnol-transcript-message ${message.sender}`} key={`${message.sender}-${index}`}>
              <span>{message.sender}</span>
              {message.timestamp && <em>{formatTranscriptTime(message.timestamp)}</em>}
              <p>{message.text}</p>
            </article>
          ))}
        </div>
      );
    }

    return (
      <div className="fnol-source-empty">
        {transcriptError || emptyLabel}
      </div>
    );
  };

  return (
    <aside className="fnol-source-pane" aria-label="FNOL source attachment">
      <header className="fnol-source-header">
        <span>
          {isCall
            ? 'Call Recording'
            : isEmail
              ? 'Email Source'
              : transcript?.title || (channel === 'CHAT' ? 'Web Chat Transcript' : 'Source Attachment')}
        </span>
        <strong>
          {isCall
            ? voiceRecordings?.session_id || channel || '-'
            : isEmail
              ? emailSource?.subject || channel || '-'
              : transcript?.session_id || channel || '-'}
        </strong>
      </header>

      {isCall ? (
        <div className="fnol-call-source-stack">
          <section className="fnol-call-source-section" aria-label="Call recordings">
            <h2>Recordings</h2>
            {isVoiceLoading ? (
              <div className="fnol-source-empty">Loading call recordings...</div>
            ) : displayVoiceRecordings.length ? (
              <div className="fnol-voice-list">
                {displayVoiceRecordings.map((recording) => (
                  <article className="fnol-voice-card" key={recording.object_path}>
                    <div>
                      <strong>{formatRecordingName(recording.file_name)}</strong>
                      <span>
                        {[recording.session_id, formatRecordingSize(recording.size), formatTranscriptTime(recording.updated)]
                          .filter(Boolean)
                          .join(' | ')}
                      </span>
                    </div>
                    <audio controls preload="metadata">
                      <source src={recording.content_url} type={recording.content_type || 'audio/wav'} />
                      <a href={recording.content_url}>Open recording</a>
                    </audio>
                  </article>
                ))}
              </div>
            ) : (
              <div className="fnol-source-empty">
                {voiceError || 'No call recording is linked for this FNOL yet.'}
              </div>
            )}
          </section>
        </div>
      ) : isEmail ? (
        isEmailLoading ? (
          <div className="fnol-source-empty">Loading email source...</div>
        ) : emailSource ? (
          <div className="fnol-email-source">
            <article className="fnol-email-card">
              <div className="fnol-email-meta">
                {emailSource.subject && (
                  <div>
                    <span>Subject</span>
                    <strong>{emailSource.subject}</strong>
                  </div>
                )}
                {emailSource.from && (
                  <div>
                    <span>From</span>
                    <strong>{emailSource.from}</strong>
                  </div>
                )}
                {emailSource.to && (
                  <div>
                    <span>To</span>
                    <strong>{emailSource.to}</strong>
                  </div>
                )}
                {emailSource.cc && (
                  <div>
                    <span>CC</span>
                    <strong>{emailSource.cc}</strong>
                  </div>
                )}
                {emailSource.date && (
                  <div>
                    <span>Date</span>
                    <strong>{formatTranscriptTime(emailSource.date) || emailSource.date}</strong>
                  </div>
                )}
              </div>
              <div className="fnol-email-body">
                <span>Email Body</span>
                <p>{formattedEmailBody || 'No email body is available for this FNOL.'}</p>
              </div>
              {emailSource.file_name && (
                <div className="fnol-email-file">
                  <span>Source File</span>
                  <strong>{emailSource.file_name}</strong>
                </div>
              )}
            </article>
          </div>
        ) : (
          <div className="fnol-source-empty">
            {emailError || 'No email source is linked for this FNOL yet.'}
          </div>
        )
      ) : (
        renderTranscript(
          'Loading chat transcript...',
          channel === 'CHAT'
            ? 'No chat transcript is linked for this FNOL yet.'
            : 'Email source previews will be connected in the next step.'
        )
      )}
    </aside>
  );
};

const FnolReviewWindow = ({ onLogout }) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const recordId = params.recordId || new URLSearchParams(location.search).get('fnolReview') || '';
  const [record, setRecord] = useState({});
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [transcript, setTranscript] = useState(null);
  const [transcriptError, setTranscriptError] = useState('');
  const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);
  const [voiceRecordings, setVoiceRecordings] = useState(null);
  const [voiceError, setVoiceError] = useState('');
  const [isVoiceLoading, setIsVoiceLoading] = useState(false);
  const [emailSource, setEmailSource] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [isEmailLoading, setIsEmailLoading] = useState(false);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.location.hostname === 'localhost' &&
      window.location.port === '3100'
    ) {
      window.location.replace(`http://127.0.0.1:3100${window.location.pathname}${window.location.search}`);
    }
  }, []);

  useEffect(() => {
    let active = true;

    const loadRecord = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const result = await fetchFirstFnolJson(`/fnol-records/${encodeURIComponent(recordId)}`);
        let loadedRecord = result.form_data || result.automation || {};
        if (isCallRecord(recordId, loadedRecord)) {
          loadedRecord = {
            ...loadedRecord,
            interaction_channel: 'CALL',
            claim_id: firstValue(loadedRecord, ['claim_id', 'claimId', 'claimid']) || deriveClaimIdFromFnolId(recordId),
            fnol_id: loadedRecord.fnol_id || deriveFnolIdFromClaimId(recordId),
          };
        } else if (isMailRecord(recordId, loadedRecord)) {
          loadedRecord = normalizeMailRecordFields(recordId, loadedRecord);
        }
        if (active) setRecord(loadedRecord);
      } catch (error) {
        if (isCallRecord(recordId) || !isMailRecord(recordId)) {
          if (active) setLoadError(error.message || 'Unable to load FNOL.');
          return;
        }

        try {
          const source = await loadFirstEmailSource(recordId);
          const fallbackRecord = buildMailRecordFromEmailSource(recordId, source);
          if (active) {
            setRecord(fallbackRecord);
            setEmailSource(source);
            setLoadError('');
          }
        } catch (sourceError) {
          if (active) setLoadError(sourceError.message || error.message || 'Unable to load FNOL.');
        }
      } finally {
        if (active) setIsLoading(false);
      }
    };

    loadRecord();
    return () => {
      active = false;
    };
  }, [recordId]);

  const channel = useMemo(
    () => getRecordChannel(recordId, record),
    [recordId, record]
  );

  useEffect(() => {
    let active = true;

    const loadTranscript = async () => {
      if (!recordId) return;
      if (channel === 'CALL') {
        setIsTranscriptLoading(false);
        setTranscriptError('');
        setTranscript(null);
        return;
      }
      setIsTranscriptLoading(true);
      setTranscriptError('');
      setTranscript(null);

      try {
        const result = await fetchFirstFnolJson(
          `/fnol-records/${encodeURIComponent(recordId)}/transcript`,
          { timeoutMs: 45000 }
        );
        if (active) setTranscript(result);
      } catch (error) {
        if (active) setTranscriptError('');
      } finally {
        if (active) setIsTranscriptLoading(false);
      }
    };

    loadTranscript();
    return () => {
      active = false;
    };
  }, [recordId, channel]);

  useEffect(() => {
    let active = true;
    const linkedIds = [recordId, record.fnol_id, record.claim_id];

    const loadVoiceRecordings = async () => {
      setVoiceRecordings(null);
      setVoiceError('');

      if (channel !== 'CALL' || !recordId) return;

      if (!hasKnownVoiceRecording(...linkedIds)) {
        setVoiceError('No call recording is linked for this FNOL yet.');
        return;
      }

      setIsVoiceLoading(true);
      try {
        const result = await fetchFnolVoiceRecordings(recordId);
        if (active) setVoiceRecordings(result);
      } catch (error) {
        if (active) setVoiceError(error.message || 'Unable to load call recordings.');
      } finally {
        if (active) setIsVoiceLoading(false);
      }
    };

    loadVoiceRecordings();
    return () => {
      active = false;
    };
  }, [channel, recordId, record.fnol_id, record.claim_id]);

  useEffect(() => {
    let active = true;

    const loadEmailSource = async () => {
      setEmailSource(null);
      setEmailError('');

      const emailLookupRecord = {
        fnol_id: record.fnol_id,
        claim_id: record.claim_id,
        claimid: record.claimid,
        policy_number: record.policy_number,
        Policy_Number: record.Policy_Number,
      };

      if (channel !== 'EMAIL' || getEmailSourceIds(recordId, emailLookupRecord).length === 0) return;

      setIsEmailLoading(true);
      try {
        const result = await loadFirstEmailSource(recordId, emailLookupRecord);
        if (active) setEmailSource(result);
      } catch (error) {
        if (active) setEmailError(error.message || 'Unable to load email source.');
      } finally {
        if (active) setIsEmailLoading(false);
      }
    };

    loadEmailSource();
    return () => {
      active = false;
    };
  }, [channel, recordId, record.fnol_id, record.claim_id, record.claimid, record.policy_number, record.Policy_Number]);

  const activeLineTab = useMemo(
    () => normalizeLineOfBusiness(firstValue(record, ['line_of_business', 'lob', 'loss_type', 'type_of_loss'])),
    [record]
  );
  const fallbackTranscript = getFnolTranscript(recordId, record.fnol_id, record.claim_id);
  const displayedTranscript = transcript || fallbackTranscript;
  const goToMainPage = () => {
    navigate('/claims');
  };

  return (
    <div className="dashboard-page fnol-page fnol-review-page">
      <Header onLogout={onLogout} />

      <main className="fnol-review-shell">
        <section className="fnol-review-detail" aria-label="FNOL detail">
          <header className="fnol-review-title">
            <div>
              <span>FNOL Review</span>
              <h1>{record.fnol_id || recordId}</h1>
            </div>
            <div className="fnol-review-title-actions">
              {record.claim_id && <strong>{record.claim_id}</strong>}
              <button type="button" className="fnol-review-back-button" onClick={goToMainPage}>
                <i className="fas fa-arrow-left" aria-hidden="true"></i>
                <span>Back to Main Page</span>
              </button>
            </div>
          </header>

          {isLoading ? (
            <div className="fnol-review-state">Loading FNOL...</div>
          ) : loadError ? (
            <div className="fnol-review-state error">{loadError}</div>
          ) : (
            <ClaimForm data={record} activeLineTab={activeLineTab} showEditBar={false} />
          )}
        </section>

        <SourcePane
          record={record}
          recordId={recordId}
          sourceChannel={channel}
          transcript={displayedTranscript}
          isTranscriptLoading={isTranscriptLoading && !displayedTranscript}
          transcriptError={transcriptError}
          voiceRecordings={voiceRecordings}
          isVoiceLoading={isVoiceLoading}
          voiceError={voiceError}
          emailSource={emailSource}
          isEmailLoading={isEmailLoading}
          emailError={emailError}
        />
      </main>

      <Footer />
    </div>
  );
};

export default FnolReviewWindow;
